import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { addWaste, fetchClients } from '../../../store/actions/waste';
import { fetchWarehouses } from '../../../store/actions/warehouse';
import { fetchContainers } from '../../../store/actions/container';

import { useFormik } from 'formik';
import { validation, formValues } from './validationSchema';

import { errorHelper, Loader } from '../../../utils/tools';
import { loadingCodes } from '../../../utils/constants/loadingCodes';
import { acronyms } from '../../../utils/constants/acronyms';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Row, Col, InputGroup } from 'react-bootstrap';

import CustomModalLarge from '../../../hoc/customModalLarge';
import AddClient from '../../client/add';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { sk } from 'date-fns/locale';

const AddIncomeWaste = ({branchId, evidenceListId, onClose, onAdded, copiedData}) => {

    const today = Date.now();

    const clients = useSelector(state => state.waste.clients);
    const warehouses = useSelector(state => state.branch.warehouse);
    const containers = useSelector(state => state.branch.container)
    const loading = useSelector(state => state.waste.loading);
    const currentEvidenceList = useSelector(state => state.evidenceList.current);

    const currentBranch = branchId;
    const currentEvidenceListId = evidenceListId;

    const dispatch = useDispatch();

    const [isAddClientModalVisible, setIsAddClientModalVisible] = useState(false);

    const formik = useFormik({
        currentEvidenceListId,
        initialValues: formValues,
        validationSchema: validation,
        onSubmit: (values) => {
            handleSubmit(values)
        }
    });

    useEffect(() => {
        if (copiedData) {
            formik.setValues({
                date: copiedData.date || Date.now(),
                warehouse: copiedData.warehouse || '',
                container: copiedData.container || '',
                income: copiedData.income ?? 0,
                disposal: copiedData.disposal ?? 0,
                loadingCode: copiedData.loadingCode || '',
                client: copiedData.client?._id || copiedData.client || '',
                clientBranchId: copiedData.clientBranchId?.toString() || '',
                note: copiedData.note || '',
                acronym: copiedData.acronym || '',
                weight: 0,
            });
        } else {
            formik.setValues(formValues);
        }
    }, [copiedData]);

    // Fetch user's clients, warehouses & containers
    useEffect(()=> {
        dispatch(fetchClients('WASTE'));
        if(currentBranch) {
            dispatch(fetchWarehouses(currentBranch));
            dispatch(fetchContainers(currentBranch));
        }
    },[dispatch, currentBranch]);

    useEffect(() => {
        if (!isAddClientModalVisible) return;
        const stopEsc = (e) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                setIsAddClientModalVisible(false);
            }
        };
        document.addEventListener('keydown', stopEsc, true);
        return () => document.removeEventListener('keydown', stopEsc, true);
    }, [isAddClientModalVisible]);

    const handleClientCreated = async (clientId) => {
        await dispatch(fetchClients('WASTE'));
        formik.setFieldValue('client', clientId);
        formik.setFieldValue('clientBranchId', '');
        setIsAddClientModalVisible(false);
    };

    const handleSubmit = (values) => {
        // form action
        values.currentEvidenceListId = currentEvidenceListId;
        if(currentBranch !== '')
            values.branchId = currentBranch;
        dispatch(addWaste(values)).unwrap().then(() => {
            formik.resetForm();
            if (evidenceListId && branchId) {
                onAdded?.();
                onClose();
            }
        })
        .catch(error => {
            console.error("Error adding waste:", error);
        });
    }
  
    return(
        <>
            { loading ?
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '35vh' 
                }}>
                    <Loader />
                </div>
            :
            <Form
                component="form"
                onSubmit={formik.handleSubmit}
                id="addWasteForm"
            >       
                    <h2>Pridať vznik/príjem odpadu</h2>
                    <hr />

                    <Row>

                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Date */}
                            <Form.Group className="mb-3" controlId="date">
                                <Form.Label>Dátum*</Form.Label>
                                <div className="datepicker-container">
                                    <DatePicker
                                        id="date"
                                        name="date"
                                        showIcon
                                        toggleCalendarOnIconClick
                                        maxDate={today}
                                        selected={formik.values.date}
                                        onChange={(date) => formik.setFieldValue('date', date)}
                                        dateFormat="dd.MM.yyyy" // Príklad formátu dátumu, môžete prispôsobiť podľa potreby
                                        className={`form-control ${errorHelper(formik, 'date').error ? 'is-invalid' : ''}`}
                                        locale={sk}
                                        />
                                </div>
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'date').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Warehouse */}
                            <Form.Group className="mb-3" controlId="formBasicWarehouse">
                                <Form.Label>Sklad</Form.Label>
                                <Form.Select
                                    aria-label="Sklad:"
                                    {...formik.getFieldProps('warehouse')}
                                    isInvalid={errorHelper(formik, 'warehouse').error}
                                >
                                <option value="" disabled>
                                    Vyberte sklad:
                                </option>


                                { warehouses.map((warehouse, index) => (
                                    <option key={index} value={warehouse}>
                                        {warehouse}
                                    </option>
                                ))}

                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'warehouse').helperText}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Container */}
                            <Form.Group className="mb-3" controlId="formBasicContainer">
                                <Form.Label>Nádoba</Form.Label>
                                <Form.Select
                                    aria-label="Nádoba:"
                                    {...formik.getFieldProps('container')}
                                    isInvalid={errorHelper(formik, 'container').error}
                                >
                                <option value="" disabled>
                                    Vyberte nádobu:
                                </option>


                                { containers.map((container, index) => (
                                    <option key={index} value={container}>
                                        {container}
                                    </option>
                                ))}

                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'container').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Income */}
                            <Form.Group className="mb-3" controlId="formBasicIncome">
                                <Form.Label>Hmotnosť (t)*</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Vznik/Príjem:"
                                    {...formik.getFieldProps('income')}
                                    isInvalid={errorHelper(formik, 'income').error} // Priraď isInvalid na základe error hodnoty z errorHelper
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'income').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                    </Row>

                    <Row>
                        {/* Loading Code */}
                        <Col xs={12} sm={12} md={12} lg={12}>
                            <Form.Group className="mb-3" controlId="formBasicLoadingCode">
                                <Form.Label>Kód nakladania*</Form.Label>
                                <Form.Select
                                    aria-label="Vyberte kód nakladania:"
                                    {...formik.getFieldProps("loadingCode")}
                                    isInvalid={errorHelper(formik, "loadingCode").error}
                                >
                                    <option value="" disabled>
                                        Vyberte kód nakladania:
                                    </option>

                                    {Object.entries(loadingCodes).map(([category, codes]) => {
                                        const filtered = currentEvidenceList?.activityCode === 'R' && currentEvidenceList?.primaryLoadingCode
                                            ? codes.filter(c => {
                                                const isRestrictedR = /^R([1-9]|10|11)$/.test(c.value);
                                                return !isRestrictedR || c.value === currentEvidenceList.primaryLoadingCode;
                                            })
                                            : codes;
                                        if (filtered.length === 0) return null;
                                        return (
                                            <optgroup key={category} label={category}>
                                                {filtered.map((loadingCode) => (
                                                    <option key={loadingCode.value} value={loadingCode.value}>
                                                        {loadingCode.value} - {loadingCode.label}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    })}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, "loadingCode").helperText}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        {/* Company */}
                        <Col xs={12} sm={12} md={12} lg={12}>
                            <Form.Group className="mb-3" controlId="formBasicClient">
                                <Form.Label>Dodávateľ*</Form.Label>
                                <InputGroup hasValidation>
                                    <Form.Select
                                        aria-label="Dodávateľ:"
                                        {...formik.getFieldProps('client')}
                                        isInvalid={errorHelper(formik, 'client').error}
                                        onChange={e => {
                                            formik.setFieldValue('client', e.target.value);
                                            formik.setFieldValue('clientBranchId', '');
                                        }}
                                    >
                                        <option value="" disabled>
                                            Vyberte dodávateľa:
                                        </option>
                                        { clients.map((client, index) => (
                                            <option key={index} value={client._id}>
                                                {client.companyName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setIsAddClientModalVisible(true)}
                                        title="Pridať nového dodávateľa"
                                    >
                                        + Pridať
                                    </Button>
                                    <Form.Control.Feedback type="invalid">
                                        {errorHelper(formik, 'client').helperText}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        {/* Branch */}
                        {(() => {
                            const selectedClient = clients.find(c => c._id === formik.values.client);
                            const branches = selectedClient?.branches?.filter(b => b.active) || [];
                            if (branches.length === 0) return null;
                            return (
                                <Col xs={12} sm={12} md={12} lg={12}>
                                    <Form.Group className="mb-3" controlId="formBasicClientBranch">
                                        <Form.Label>Prevádzkareň</Form.Label>
                                        <Form.Select
                                            aria-label="Prevádzkareň:"
                                            {...formik.getFieldProps('clientBranchId')}
                                        >
                                            <option value="">Sídlo (hlavná adresa)</option>
                                            {branches.map(branch => (
                                                <option key={branch._id} value={branch._id}>
                                                    {branch.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            );
                        })()}


                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Note */}
                            <Form.Group className="mb-3" controlId="formBasicNote">
                                <Form.Label>Poznámka</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Poznámka:"
                                    {...formik.getFieldProps('note')}
                                    isInvalid={errorHelper(formik, 'note').error} // Priraď isInvalid na základe error hodnoty z errorHelper
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'note').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={12} md={12} lg={12}>
                            {/* Acronym */}
                            <Form.Group className="mb-3" controlId="formBasicAcronym">
                                <Form.Label>Skratka</Form.Label>
                                <Form.Select
                                    aria-label="Vyberte skratku:"
                                    {...formik.getFieldProps('acronym')}
                                    isInvalid={errorHelper(formik, 'acronym').error}
                                >
                                    <option value="" disabled>
                                        Vyberte skratku:
                                    </option>

                                    {acronyms.map((acronym) => (
                                        <option key={acronym.value} value={acronym.value}>
                                            {acronym.label}
                                        </option>
                                    ))}

                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errorHelper(formik, 'acronym').helperText}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                    </Row>                   
                        
                    <Row className="mt-auto">
                        <hr />
                        <Col className="d-flex justify-content-end">
                            <Button
                                className='button-custom-large' 
                                variant='primary' 
                                type="submit" 
                                size="lg" 
                                style={{ marginTop: 'auto' }}
                            >
                                Pridať odpad
                            </Button>
                        </Col>
                    </Row>

            </Form>
        }

        <CustomModalLarge
            isVisible={isAddClientModalVisible}
            onClose={() => setIsAddClientModalVisible(false)}
        >
            <AddClient
                isVisible={isAddClientModalVisible}
                onClose={() => setIsAddClientModalVisible(false)}
                onSuccess={handleClientCreated}
                initialCategories={['WASTE']}
            />
        </CustomModalLarge>
        </>
    );
}

export default AddIncomeWaste;

